import { useQuery } from '@apollo/client';
import i18n from '@risksmart-app/i18n/src/i18n';
import type { ActionRegisterResponse } from '@risksmart-app/trpc/src/types';
import type {
  Action_Bool_Exp,
  Control_Bool_Exp,
  GetActionsQuery,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  GetActionsDocument,
  GetControlsDocument,
  Parent_Type_Enum,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import type { UseQueryOptions } from '@tanstack/react-query';
import { useMemo } from 'react';
import { mapTrpcActionsToGraphQL } from 'src/hooks/queries/action/useGetActionsRegister';

import useEntityWhereFilter from '@/hooks/useEntityWhereFilter';
import type { StatefulTableOptions } from '@/utils/table/hooks/useGetStatelessTableProps';
import type { TableFields, TableRecord } from '@/utils/table/types';

import { UNRATED } from '../../gigawidget/types';
import { createDataSource } from '../createDataSource';
import { defaultClickthroughFilter } from '../dataSourceHelpers';
import { simpleField, useRelationshipTableProps } from './relationship-helpers';

export type ControlActionRow = TableRecord & {
  Id: string;
  ControlId: string;
  ControlTitle: string;
  ControlType: string;
  ActionId: string;
  ActionTitle: string;
  ActionStatus: string;
  ActionPriority: string;
};

export default createDataSource({
  group: 'relationship',
  hasAccess: () => true,
  parentTypes: [Parent_Type_Enum.Control, Parent_Type_Enum.Action],
  documentNode: GetActionsDocument,
  trpcQuery: (trpc) => {
    const queryOptions = trpc.frontend.action.register.queryOptions({});
    const transformedQueryOptions = {
      ...queryOptions,
      select: (data: ActionRegisterResponse) => mapTrpcActionsToGraphQL(data),
    };

    return transformedQueryOptions as UseQueryOptions<unknown, Error>;
  },
  useDefaultVariables: () => ({
    where: useEntityWhereFilter<Action_Bool_Exp>(Parent_Type_Enum.Action),
  }),
  useTablePropsHook: (
    data: GetActionsQuery | undefined,
    options: StatefulTableOptions<ControlActionRow>
  ) => {
    const fields = useMemo<TableFields<ControlActionRow>>(
      () => ({
        ControlTitle: simpleField(i18n.t('controls.columns.title')),
        ControlType: simpleField(i18n.t('controls.columns.type')),
        ActionTitle: simpleField(i18n.t('actions.columns.title')),
        ActionStatus: simpleField(i18n.t('actions.columns.status')),
        ActionPriority: simpleField(i18n.t('actions.columns.priority')),
      }),
      []
    );

    const controlWhere = useEntityWhereFilter<Control_Bool_Exp>(
      Parent_Type_Enum.Control
    );
    const { data: controlData } = useQuery(GetControlsDocument, {
      variables: { where: controlWhere },
    });

    const joinedData = useMemo(() => {
      if (!data?.action || !controlData?.control) {
        return undefined;
      }

      const controlMap = new Map(controlData.control.map((c) => [c.Id, c]));

      return data.action.flatMap((action) =>
        action.parents
          .filter(
            (p) =>
              p.parent?.ObjectType === Parent_Type_Enum.Control && p.parent.Id
          )
          .map((parent) => {
            const control = controlMap.get(parent.parent!.Id);

            return {
              Id: `${parent.parent!.Id}_${action.Id}`,
              ControlId: parent.parent!.Id,
              ControlTitle: control?.Title ?? parent.control?.Title ?? '',
              ControlType: control?.Type ?? '',
              ActionId: action.Id,
              ActionTitle: action.Title ?? '',
              ActionStatus: action.Status ?? '',
              ActionPriority: String(action.Priority ?? ''),
            };
          })
      );
    }, [data?.action, controlData?.control]);

    return useRelationshipTableProps(
      joinedData,
      fields,
      i18n.t('control_action_other'),
      options
    );
  },
  entityNamePlural: 'control_action_other',
  entityNameSingular: 'control_action_one',
  fields: 'controlActions.fields',
  dashboardFilterConfig: {},
  categoryGetters: [
    {
      id: 'controlType',
      name: () => i18n.t('controls.columns.type'),
      categoryGetter: (item: ControlActionRow) => item.ControlType || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('ControlType'),
    },
    {
      id: 'actionStatus',
      name: () => i18n.t('actions.columns.status'),
      categoryGetter: (item: ControlActionRow) => item.ActionStatus || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('ActionStatus'),
      ratingColourKey: 'action_status',
    },
    {
      id: 'actionPriority',
      name: () => i18n.t('actions.columns.priority'),
      categoryGetter: (item: ControlActionRow) =>
        item.ActionPriority || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('ActionPriority'),
      ratingColourKey: 'priority',
    },
    {
      id: 'controlTitle',
      name: () => i18n.t('controls.columns.title'),
      categoryGetter: (item: ControlActionRow) => item.ControlTitle || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('ControlTitle'),
    },
    {
      id: 'actionTitle',
      name: () => i18n.t('actions.columns.title'),
      categoryGetter: (item: ControlActionRow) => item.ActionTitle || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('ActionTitle'),
    },
  ],
});
